import { FingerPosition } from './profile-biometrics.model';

/**
 * Cadastro de digital no sensor embarcado da catraca (Toletus LiteNet 2).
 *
 * É um protocolo diferente do leitor USB, não uma variação dele: aqui o front
 * dispara **um** POST e a placa faz sozinha as três leituras, a validação e a
 * comparação. Não existe captura, `IsValidTemplate` nem `VerifyMatched` do lado
 * do cliente, e nenhum byte biométrico chega ao browser durante o cadastro.
 *
 * ## A caixa é PascalCase, como todo o resto do repositório
 *
 * O guia de integração do backend mostra os exemplos em camelCase, e isso é
 * notação JS informal do documento — não o contrato. Os endpoints REST vizinhos
 * do **mesmo** `accessdirectoryservice` (`AccessPointId`, `EquipmentId`,
 * `TenantId`) e os do `fingerprintservice` (`PersonId`, `FingerPosition`)
 * serializam PascalCase, que é o que o ASP.NET faz por omissão neste ecossistema.
 */

/** Corpo do POST de cadastro. `RequestId` volta como correlação no progresso. */
export interface EmbeddedEnrollRequest {
  PersonId: string;
  FingerPosition: FingerPosition;
  RequestId: string;
}

/** Corpo do cancelamento. O `RequestId` é o mesmo enviado no cadastro. */
export interface EmbeddedEnrollCancelRequest {
  RequestId: string;
}

/**
 * Desfechos conhecidos.
 *
 * `Enrolled` e `SavedPendingWrite` chegam em `200` e são **ambos sucesso**:
 * no segundo o servidor já tem a digital e a catraca recebe-a na sincronização
 * seguinte. Pedir recaptura nesse caso faria o atendente repetir um cadastro que
 * já está feito.
 */
export enum EmbeddedEnrollOutcome {
  Enrolled = 'Enrolled',
  SavedPendingWrite = 'SavedPendingWrite',
  Duplicate = 'Duplicate',
  TimedOut = 'TimedOut',
  NoReader = 'NoReader',
  SensorNotReady = 'SensorNotReady',
  CaptureFailed = 'CaptureFailed',
  RejectedByServer = 'RejectedByServer',
  Cancelled = 'Cancelled',
}

/**
 * O `Outcome` é `string` e não o enum porque o contrato diz explicitamente que a
 * lista pode ganhar valores. Um valor desconhecido é falha genérica que mostra a
 * `Message` crua — nunca um `switch` que cai em silêncio no caso feliz.
 */
export interface EmbeddedEnrollResponse {
  Outcome: EmbeddedEnrollOutcome | string;
  SlotId?: number | null;
  Message?: string | null;
}

/** Os dois desfechos de `200`. Usado para decidir sucesso sem repetir literais. */
export const EMBEDDED_ENROLL_SUCCESS_OUTCOMES: readonly string[] = [
  EmbeddedEnrollOutcome.Enrolled,
  EmbeddedEnrollOutcome.SavedPendingWrite,
];

export function isEmbeddedEnrollSuccess(
  outcome: string | null | undefined
): boolean {
  return !!outcome && EMBEDDED_ENROLL_SUCCESS_OUTCOMES.includes(outcome);
}

/**
 * Uma digital embarcada, já limpa para uso no front.
 *
 * **Três campos do fio ficam deliberadamente de fora:**
 * - `Template` são os bytes biométricos. Não se exibe, não se regista, não se
 *   persiste — descartado no mapeamento, para não haver como escapar.
 * - `MacAddress` e `SyncedOn` vêm **sempre vazios** neste endpoint, porque o
 *   template pertence à pessoa e ao dedo, não ao equipamento. Construir UI sobre
 *   eles seria mostrar um vazio permanente como se fosse informação.
 */
export interface EmbeddedTemplate {
  EmbeddedTemplateId: string;
  PersonId: string;
  SlotId: number | null;
  FingerPosition: FingerPosition;
  IsDeleted: boolean;
}

/** A resposta crua da listagem, antes de descartar o `Template`. */
export interface EmbeddedTemplateWire {
  EmbeddedTemplateId: string;
  PersonId: string;
  SlotId?: number | null;
  FingerPosition: FingerPosition;
  Template?: string;
  IsDeleted?: boolean;
  MacAddress?: string;
  SyncedOn?: string | null;
}
